import { type RefObject, useLayoutEffect, useState } from 'react';

/**
 * Determines whether the given element extends beyond the given parent element.
 * If the element extends beyond the parent element, rotate the element.
 *
 * The given element should have absolute position.
 */
export const useOverflowPosition = (
  elementRef: RefObject<HTMLElement>,
  relativeElement: HTMLElement | null,
): { left?: string; right?: string } | undefined => {
  const [style, setStyle] = useState<
    { left?: string; right?: string } | undefined
  >(undefined);
  const prevRect = elementRef.current?.getBoundingClientRect();

  // biome-ignore lint/correctness/useExhaustiveDependencies: complex dependencies, works this way, not worthy fixing
  useLayoutEffect(() => {
    if (!elementRef.current || !relativeElement) {
      return;
    }

    const isCurrentRightOverflow =
      elementRef.current.getBoundingClientRect().right >
      relativeElement.getBoundingClientRect().left +
        relativeElement.clientWidth;
    const isPrevRightOverflow =
      prevRect &&
      prevRect.right !== undefined &&
      prevRect.right >
        relativeElement.getBoundingClientRect().left +
          relativeElement.clientWidth;
    const isRightOverflow = isCurrentRightOverflow || isPrevRightOverflow;

    const isCurrentLeftOverflow =
      elementRef.current.getBoundingClientRect().left <
      relativeElement.getBoundingClientRect().left;
    const isPrevLeftOverflow =
      prevRect &&
      prevRect.left !== undefined &&
      prevRect.left < relativeElement.getBoundingClientRect().left;
    const isLeftOverflow = isCurrentLeftOverflow || isPrevLeftOverflow;

    if (isRightOverflow && isLeftOverflow) {
      setStyle({ left: '0px', right: 'auto' });
    } else if (isLeftOverflow) {
      setStyle({ left: '0px', right: 'auto' });
    } else if (isRightOverflow) {
      setStyle({ left: 'auto', right: '0px' });
    }
  }, [elementRef.current?.getBoundingClientRect()?.right]);

  return style;
};
